import React from 'react'
import { ActionPlan, ActionPlanStatus, DemandAnalysis, DemandAnalysisQuestion, DemandAnalysisQuestionStatus } from '../api/schemas/schema'
import { t } from '../i18n/i18n'
import { api } from './apiHelper'
import { RTFOutput } from '../components/common/outputs/RTFOutput'
import { IGroupedTableColumnDefintion } from '../interfaces/IGroupedTableColumnDefintion'
import { GroupHeader } from '../components/common/list/GroupHeader'
import { BadgeActionPlanStatus } from '../components/demandAnalysis/BadgeActionPlanStatus'
import { formatToSwedishCrowns } from './stringHelper'
import { IGroup } from '../interfaces/IGroup'
import { ISummaryField } from '../interfaces/ISummaryField'
import { BadgeComplianceLevel } from '../components/demandAnalysis/BadgeComplianceLevel'
import { IItemState } from '../interfaces/IItemState'
import { Text } from '@fluentui/react-components'

export const filterDemandActionPlansByProcess = (actionPlans: ActionPlan[], questions: DemandAnalysisQuestion[], selectedProcess: string) => {
   if (!actionPlans || !questions) return []
   if (selectedProcess === t('AllProcesses')) {
      return actionPlans
   } else {
      return actionPlans.filter((x) => findQuestionParentToActionPlan(questions, x)?.processes.some((p) => p === selectedProcess))
   }
}

export const findQuestionParentToActionPlan = (questions: DemandAnalysisQuestion[], actionPlan: ActionPlan) =>
   questions.find((q) => q.id === actionPlan.demandAnalysisQuestionId)

export const getQuestionsWithActionPlan = (questions: DemandAnalysisQuestion[]): DemandAnalysisQuestion[] =>
   (questions ?? []).filter((q) => q.status === DemandAnalysisQuestionStatus.Determined && q.answer?.actionRequired === true)

export const getActionPlanColumns = (questions: DemandAnalysisQuestion[]): IGroupedTableColumnDefintion<ActionPlan>[] => [
   {
      columnId: 'question',
      compare: (a, b) => -1,
      renderCell: (item) => {
         let question = findQuestionParentToActionPlan(questions, item).text
         // The opening paragraph tag may carry attributes — an indented paragraph is
         // serialised as <p style="margin-left: …"> — so match the tag rather than a
         // literal '<p>'. Otherwise the number ends up outside the paragraph, on its own line.
         if (/^<p[^>]*>/.test(question)) question = question.replace(/^<p([^>]*)>/, `<p$1>${item.index}. `)
         else question = `${item.index}. ${question}`
         return <RTFOutput text={question} />
      },
      renderHeaderCell: () => t('Question'),
      renderGroupHeaderCell: (group) => <GroupHeader title={t('ComplianceLevel')} badge={<BadgeComplianceLevel text={group.label} bgHexColor={group.hex} />} />,
   },
   {
      columnId: 'priority',
      compare: (a, b) => -1,
      renderHeaderCell: () => t('Priority'),
      renderGroupHeaderCell: (group) => '',
      renderCell: (item) => <Text style={{ wordWrap: 'break-word' }}>{translateActionPlanPriority(item.priority)}</Text>,
      colMinWidth: '75px',
   },
   {
      columnId: 'process',
      compare: (a, b) => -1,
      renderCell: (item) => <Text style={{ wordWrap: 'break-word' }}>{findQuestionParentToActionPlan(questions, item).processes.join(', ')}</Text>,
      renderGroupHeaderCell: (group) => '',
      renderHeaderCell: () => t('Process'),
   },
   {
      columnId: 'estimatedCost',
      compare: (a, b) => -1,
      renderCell: (item) => {
         const question = findQuestionParentToActionPlan(questions, item)

         if (question.estimatedCost == null) {
            return <Text italic>{t('NoValueSet')}</Text>
         }
         return `${formatToSwedishCrowns(question.estimatedCost)} kr`
      },
      renderHeaderCell: () => t('EstimatedCostShort'),
      renderGroupHeaderCell: (group) => {
         let estimatedCostTotal = 0
         let hasValues = false
         group.items.forEach((x) => {
            const val = findQuestionParentToActionPlan(questions, x).estimatedCost
            if (val != null) {
               estimatedCostTotal += val
               hasValues = true
            }
         })
         return hasValues ? `${formatToSwedishCrowns(estimatedCostTotal)} kr` : <Text italic>{t('NoValueSet')}</Text>
      },
   },
   {
      columnId: 'budgetedCost',
      compare: (a, b) => -1,
      renderCell: (item) => {
         if (item.status !== ActionPlanStatus.Decided) {
            return <Text italic>{t('NoValueSet')}</Text>
         }

         if (item.budgetedCost === null) {
            return <Text italic>{t('NoValueSet')}</Text>
         }

         return `${formatToSwedishCrowns(item.budgetedCost)} kr`
      },
      renderHeaderCell: () => t('BudgetedCost'),
      renderGroupHeaderCell: (group) => {
         let budgetedCostTotal = 0
         let hasValues = false

         group.items.forEach((plan) => {
            if (plan.status === ActionPlanStatus.Decided && plan.budgetedCost != null) {
               budgetedCostTotal += plan.budgetedCost
               hasValues = true
            }
         })

         return hasValues ? `${formatToSwedishCrowns(budgetedCostTotal)} kr` : <Text italic>{t('NoValueSet')}</Text>
      },
   },
   {
      columnId: 'decisionStatus',
      compare: (a, b) => -1,
      renderCell: (item) => <BadgeActionPlanStatus status={item.status} />,
      renderHeaderCell: () => t('DecisionStatus'),
      renderGroupHeaderCell: (group) => {
         const totalAnswered = group.items.filter((item) => item.status !== ActionPlanStatus.NotDecided).length
         const totalItems = group.items.length
         // group.items.map((x) => x.status === ActionPlanStatus.Ongoing)
         return `${totalAnswered} / ${totalItems}`
      },
   },
]

export const translateActionPlanPriority = (priority: number) => {
   switch (priority) {
      case 1:
         return t('VeryHighPriority')
      case 2:
         return t('HighPriority')
      case 3:
         return t('LowPriority')
      case 4:
         return t('VeryLowPriority')
   }
}

export const submitDemandActionPlan = async (
   data: ActionPlan,
   setAnalysisState: React.Dispatch<React.SetStateAction<IItemState<DemandAnalysis>>>,
   parent: DemandAnalysisQuestion
) => {
   try {
      const response = (await api().updateActionPlanOnDemandAnalysis({ demandAnalysisId: parent.demandAnalysisId }, data)).data
      const updatedActionPlan = response.actionPlan
      const updatedQuestion = response.updatedQuestion

      setAnalysisState((prev) => {
         const actionPlans = [...prev.item.actionPlans]
         actionPlans[actionPlans.findIndex((x) => x.id === updatedActionPlan.id)] = updatedActionPlan
         const questions = [...prev.item.questions]
         questions[questions.findIndex((x) => x.id === parent.id)] = updatedQuestion

         return { ...prev, item: { ...prev.item, actionPlans, questions } }
      })

      return true
   } catch (error: any) {
      return false
   }
}

export const groupActionPlans = (actionPlans: ActionPlan[], questions: DemandAnalysisQuestion[]): IGroup<ActionPlan>[] => {
   const groups: IGroup<ActionPlan>[] = []
   actionPlans.forEach((plan) => {
      const question = questions.find((q) => q.id === plan.demandAnalysisQuestionId)
      const key = question.answer.text
      const group = groups.find((g) => g.key === key)
      if (!group) {
         groups.push({ label: key, key: key, order: question.answer.order, hex: question.answer.colorHex, expanded: true, items: [] })
      }
      groups.find((g) => g.key === key).items.push(plan)
   })
   return groups.sort((a, b) => b.order - a.order)
}

export const getActionPlanSummaryFields = (actionPlans: ActionPlan[], questions: DemandAnalysisQuestion[]): ISummaryField[] => {
   let totEstimatedCost = 0
   let hasEstimated = false
   let totBudgetedCost = 0
   let hasBudgeted = false

   actionPlans.forEach((plan) => {
      const question = questions.find((q) => q.id === plan.demandAnalysisQuestionId)

      if (question?.estimatedCost != null) {
         totEstimatedCost += question.estimatedCost
         hasEstimated = true
      }

      if (plan.status === ActionPlanStatus.Decided && plan.budgetedCost != null) {
         totBudgetedCost += plan.budgetedCost
         hasBudgeted = true
      }
   })

   const totalAnswered = actionPlans.filter((plan) => plan.status !== ActionPlanStatus.NotDecided).length
   const totalItems = actionPlans.length

   return [
      {
         label: t('TotalEstimatedCost'),
         text: hasEstimated ? `${formatToSwedishCrowns(totEstimatedCost)} kr` : <Text italic>{t('NoValueSet')}</Text>,
      },
      {
         label: t('TotalBudgetedCost'),
         text: hasBudgeted ? `${formatToSwedishCrowns(totBudgetedCost)} kr` : <Text italic>{t('NoValueSet')}</Text>,
      },
      { label: t('DecidedMulti'), text: `${totalAnswered} / ${totalItems}` },
   ]
}
